import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, ExternalLink, Star } from "lucide-react";
import { AppShell, type AppShellContext } from "@/components/app/AppShell";
import { ViolationBadge } from "@/components/audit/ViolationBadge";
import { AmazonReportText } from "@/components/customer/AmazonReportText";
import { CopyReviewUrlButton } from "@/components/customer/CopyReviewUrlButton";
import { TakedownStatusControls } from "@/components/customer/TakedownStatusControls";
import { api } from "@/lib/api";
import {
  amazonTakedownCapability,
  canAuthorizeManagedTakedowns,
  type BrandAmazonAccess,
} from "@/lib/amazonAccess";

type Review = {
  id: string;
  amazonReviewId: string;
  reviewUrl: string | null;
  rating: number;
  title: string | null;
  body: string;
  reviewerName: string | null;
  verifiedPurchase: boolean;
  reviewDate: string | null;
  createdAt: string;
  classification: {
    flaggedForTakedown: boolean;
    violations:
      | { type: string; policyClause?: string; evidence?: string }[]
      | null;
    rationale: string | null;
    takedownRequest: string | null;
    actionStatus: string;
    submittedAt: string | null;
    removedAt: string | null;
    chargeStatus: string;
  } | null;
};

type ProductDetail = {
  product: {
    id: string;
    asin: string;
    productTitle: string | null;
    brandId: string;
    brandName: string;
    addedAt: string;
    lastScrapedAt: string | null;
    monitoringEnabled: boolean;
    monitoringCadence: string | null;
    amazonUrl: string;
  };
  reviews: Review[];
};

type StatusUpdate = {
  actionStatus: string;
  submittedAt: string | null;
  removedAt: string | null;
  chargeStatus?: string | null;
};

export default function DashboardProductDetail() {
  return <AppShell>{(ctx) => <Inner ctx={ctx} />}</AppShell>;
}

function Inner({ ctx }: { ctx: AppShellContext }) {
  const { asin } = useParams<{ asin: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ProductDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (!asin) return;
    let cancelled = false;
    setData(null);
    setError(null);
    api<ProductDetail>(`/customer/products/${asin}`).then((res) => {
      if (cancelled) return;
      if (!res.ok || !res.data) {
        setError(res.error ?? "Couldn't load product.");
        return;
      }
      setData(res.data);
    });
    return () => {
      cancelled = true;
    };
  }, [asin]);

  useEffect(() => {
    if (!data || !ctx.activeBrandId) return;
    if (data.product.brandId !== ctx.activeBrandId) {
      navigate("/dashboard/products", { replace: true });
    }
  }, [ctx.activeBrandId, data, navigate]);

  function applyStatus(reviewId: string, next: StatusUpdate) {
    setData((prev) =>
      prev
        ? {
            ...prev,
            reviews: prev.reviews.map((review) =>
              review.id === reviewId && review.classification
                ? {
                    ...review,
                    classification: {
                      ...review.classification,
                      actionStatus: next.actionStatus,
                      submittedAt: next.submittedAt,
                      removedAt: next.removedAt,
                      ...(next.chargeStatus !== undefined
                        ? { chargeStatus: next.chargeStatus ?? "unbilled" }
                        : {}),
                    },
                  }
                : review
            ),
          }
        : prev
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-3xl px-6 py-16 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          {error}
        </h1>
        <Link
          to="/dashboard/products"
          className="mt-4 inline-flex text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
        >
          ← Back to products
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center gap-2 px-6 py-12 text-sm text-slate-500 dark:text-slate-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const { product, reviews } = data;
  const brand = ctx.brands.find((item) => item.id === product.brandId) ?? null;
  const flagged = reviews.filter((r) => r.classification?.flaggedForTakedown);
  const clean = reviews.filter((r) => !r.classification?.flaggedForTakedown);

  return (
    <div className="mx-auto w-full max-w-5xl px-6 py-8">
      <Link
        to="/dashboard/products"
        className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
      >
        ← Products
      </Link>

      <div className="mt-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {product.brandName}
          </div>
          <h1 className="mt-1 truncate text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            {product.productTitle || product.asin}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
            <span className="font-mono">{product.asin}</span>
            {product.monitoringEnabled && (
              <span className="inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900">
                Monitored
              </span>
            )}
            <a
              href={product.amazonUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 hover:text-slate-900 dark:hover:text-slate-100"
            >
              View on Amazon <ExternalLink className="h-3 w-3" />
            </a>
            <span>
              Last scraped{" "}
              {product.lastScrapedAt
                ? new Date(product.lastScrapedAt).toLocaleString()
                : "never"}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Tile label="Reviews scraped" value={reviews.length} />
        <Tile label="Flagged" value={flagged.length} accent="amber" />
        <Tile
          label="Submitted to Amazon"
          value={
            flagged.filter((r) => r.classification?.actionStatus === "submitted")
              .length
          }
        />
        <Tile
          label="Removed"
          value={
            flagged.filter((r) => r.classification?.actionStatus === "removed")
              .length
          }
          accent="emerald"
        />
      </div>

      {flagged.length > 0 && (
        <section className="mt-10">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Flagged reviews
          </h2>
          <div className="mt-3 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
            {flagged.map((r) => (
              <ReviewRow
                key={r.id}
                review={r}
                productAsin={product.asin}
                brand={brand}
                expanded={expanded === r.id}
                onToggle={() =>
                  setExpanded((cur) => (cur === r.id ? null : r.id))
                }
                onStatusChange={applyStatus}
              />
            ))}
          </div>
        </section>
      )}

      {clean.length > 0 && (
        <section className="mt-10">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            All other reviews ({clean.length})
          </h2>
          <div className="mt-3 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
            {clean.map((r) => (
              <ReviewRow
                key={r.id}
                review={r}
                productAsin={product.asin}
                brand={brand}
                expanded={expanded === r.id}
                onToggle={() =>
                  setExpanded((cur) => (cur === r.id ? null : r.id))
                }
                onStatusChange={applyStatus}
              />
            ))}
          </div>
        </section>
      )}

      {reviews.length === 0 && (
        <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
          No reviews scraped yet. Reviews are scanned weekly — check back in a few
          minutes if you just added this product.
        </div>
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: "amber" | "emerald";
}) {
  const cls =
    accent === "emerald"
      ? "rounded-2xl border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-900 dark:bg-emerald-950/40"
      : accent === "amber"
        ? "rounded-2xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-900 dark:bg-amber-950/40"
        : "rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900";
  const labelCls =
    accent === "emerald"
      ? "text-xs font-medium text-emerald-700 dark:text-emerald-300"
      : accent === "amber"
        ? "text-xs font-medium text-amber-700 dark:text-amber-300"
        : "text-xs font-medium text-slate-500 dark:text-slate-400";
  const valueCls =
    accent === "emerald"
      ? "mt-2 text-3xl font-semibold tracking-tight text-emerald-900 dark:text-emerald-100"
      : accent === "amber"
        ? "mt-2 text-3xl font-semibold tracking-tight text-amber-900 dark:text-amber-100"
        : "mt-2 text-3xl font-semibold tracking-tight text-slate-900 dark:text-slate-100";
  return (
    <div className={cls}>
      <div className={labelCls}>{label}</div>
      <div className={valueCls}>{value}</div>
    </div>
  );
}

type Verdict =
  | "pending"
  | "clean"
  | "flagged"
  | "watching"
  | "authorized"
  | "submitted"
  | "removed"
  | "rejected";

const PILL_STYLES: Record<Verdict, { label: string; cls: string }> = {
  pending: {
    label: "Pending",
    cls: "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
  },
  clean: {
    label: "Clean",
    cls: "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900",
  },
  flagged: {
    label: "Flagged",
    cls: "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900",
  },
  watching: {
    label: "Watching",
    cls: "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
  },
  authorized: {
    label: "Authorized · awaiting filing",
    cls: "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-900",
  },
  submitted: {
    label: "Submitted",
    cls: "bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:ring-blue-900",
  },
  removed: {
    label: "Removed",
    cls: "bg-emerald-100 text-emerald-800 ring-emerald-300 dark:bg-emerald-900/50 dark:text-emerald-200 dark:ring-emerald-800",
  },
  rejected: {
    label: "Rejected",
    cls: "bg-red-50 text-red-700 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900",
  },
};

function verdictOf(review: Review): Verdict {
  const c = review.classification;
  if (!c) return "pending";
  const a = c.actionStatus;
  if (a === "removed") return "removed";
  if (a === "submitted") return "submitted";
  if (a === "authorized") return "authorized";
  if (a === "watching") return "watching";
  if (a === "rejected") return "rejected";
  return c.flaggedForTakedown ? "flagged" : "clean";
}

function StatusPill({ verdict }: { verdict: Verdict }) {
  const { cls, label } = PILL_STYLES[verdict];
  return (
    <span
      className={`inline-flex shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${cls}`}
    >
      {label}
    </span>
  );
}

function ReviewRow({
  review,
  productAsin,
  brand,
  expanded,
  onToggle,
  onStatusChange,
}: {
  review: Review;
  productAsin: string;
  brand: BrandAmazonAccess | null;
  expanded: boolean;
  onToggle: () => void;
  onStatusChange: (reviewId: string, next: StatusUpdate) => void;
}) {
  const verdict = verdictOf(review);
  const violations = review.classification?.violations ?? [];
  const takedownsReady = Boolean(
    brand && canAuthorizeManagedTakedowns(brand)
  );

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start gap-4 px-5 py-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60"
      >
        <div className="flex w-24 shrink-0 flex-col items-start gap-1">
          <StarRating rating={review.rating} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3 text-xs text-slate-500 dark:text-slate-400">
            <span>
              {review.verifiedPurchase ? "Verified" : "Unverified"}
              {review.reviewerName ? ` · ${review.reviewerName}` : ""}
              {review.reviewDate
                ? ` · ${new Date(review.reviewDate).toLocaleDateString()}`
                : ""}
            </span>
            {review.reviewUrl && (
              <span className="inline-flex items-center gap-1">
                <a
                  href={review.reviewUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                >
                  View on Amazon <ExternalLink className="h-3 w-3" />
                </a>
                <CopyReviewUrlButton url={review.reviewUrl} />
              </span>
            )}
          </div>
          {review.title && (
            <div className="mt-1 truncate font-medium text-slate-900 dark:text-slate-100">
              {review.title}
            </div>
          )}
          <p className="mt-1 line-clamp-2 text-sm text-slate-600 dark:text-slate-400">
            {review.body}
          </p>
          {violations.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {violations.map((v, i) => (
                <ViolationBadge key={i} type={v.type} />
              ))}
            </div>
          )}
        </div>
        <div className="ml-2 flex shrink-0">
          <StatusPill verdict={verdict} />
        </div>
      </button>
      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-900/60">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Full review
          </div>
          <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
            "{review.body}"
          </p>
          {review.classification?.rationale && (
            <>
              <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Why we flagged this
              </div>
              <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                {review.classification.rationale}
              </p>
            </>
          )}
          {review.classification?.flaggedForTakedown && (
            <>
              {takedownsReady ? (
                <AmazonReportText review={{ ...review, asin: productAsin }} />
              ) : (
                <div className="mt-4 rounded-xl bg-amber-50 p-4 text-sm text-amber-900 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-900">
                  {brand &&
                  amazonTakedownCapability(brand) ===
                    "unverified_no_brand_registry"
                    ? "Managed takedowns are not yet supported for this brand without Brand Registry. Monitoring remains active."
                    : "Complete this brand's Brand Registry setup before using managed takedowns."}
                  {brand && (
                    <Link
                      to={`/dashboard/onboarding?brand=${encodeURIComponent(brand.id)}`}
                      className="ml-1 font-medium underline underline-offset-4"
                    >
                      View Amazon setup
                    </Link>
                  )}
                </div>
              )}
              {brand &&
                review.classification.actionStatus === "authorized" && (
                  <TakedownStatusControls
                    reviewId={review.id}
                    actionStatus={review.classification.actionStatus}
                    onChange={(next) => onStatusChange(review.id, next)}
                    className="mt-4"
                  />
                )}
            </>
          )}
          {review.classification && !review.classification.flaggedForTakedown && (
            <>
              <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Classification
              </div>
              <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                The classifier ran and did not find a policy violation.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function StarRating({ rating }: { rating: number }) {
  const safe = Math.max(0, Math.min(5, Math.round(rating)));
  return (
    <span
      className="inline-flex items-center gap-0.5"
      aria-label={`${safe} out of 5 stars`}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-3.5 w-3.5 ${
            n <= safe ? "fill-amber-400 text-amber-400" : "fill-slate-200 text-slate-200 dark:fill-slate-700 dark:text-slate-700"
          }`}
        />
      ))}
    </span>
  );
}
